import React, { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import exportingModule from "highcharts/modules/exporting";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import exportDataModule from "highcharts/modules/export-data";

import LoadingBlock from "../LoadingBlock/LoadingBlock";
import { baseChartOptions } from "./charts.config";

function initModule(mod, H) {
  const fn = mod?.default || mod;
  if (typeof fn === "function") fn(H);
}

initModule(exportingModule, Highcharts);
initModule(offlineExportingModule, Highcharts);
initModule(exportDataModule, Highcharts);

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function truncate(str, max = 15) {
  const s = String(str ?? "");
  return s.length > max ? s.slice(0, max) + "…" : s;
}

export default function SourcesPerMarketingValueBarChart({
  data = [],
  loading = false,
  title = "Top veículos por valoração",
  filename = "top-veiculos-valoracao",
  topN = 10,
  exportMode = false,
}) {
  const chartRef = useRef(null);

  const { categories, values, counts } = useMemo(() => {
    const arr = Array.isArray(data) ? data : [];
    const sliced = arr
      .filter((x) => toNum(x?.total) > 0)
      .sort((a, b) => toNum(b?.total) - toNum(a?.total))
      .slice(0, topN);

    return {
      categories: sliced.map((x) => String(x?.name || "—")),
      values: sliced.map((x) => toNum(x?.total)),
      counts: sliced.map((x) => toNum(x?.count)),
    };
  }, [data, topN]);

  const CARD_MIN_H = 360;
  const CHART_H = 380;

  const [chartKey, setChartKey] = useState(0);
  useEffect(() => {
    const onEnd = () => setChartKey((k) => k + 1);
    window.addEventListener("dashboard-export:end", onEnd);
    return () => window.removeEventListener("dashboard-export:end", onEnd);
  }, []);

  const wrapStyle = useMemo(() => {
    if (!exportMode) {
      return {
        position: "relative",
        borderRadius: 12,
        padding: 12,
        border: "none",
        background: "var(--background-graphic)",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.05)",
        overflow: "hidden",
        minHeight: CARD_MIN_H,
      };
    }

    return {
      position: "relative",
      borderRadius: 0,
      padding: 0,
      border: "none",
      background: "var(--background-graphic)",
      boxShadow: "none",
      overflow: "hidden",
      minHeight: 560,
      width: "100%",
    };
  }, [exportMode]);

  const options = useMemo(() => {
    return {
      ...baseChartOptions,

      chart: {
        type: "bar",
        height: exportMode ? 560 : CHART_H,
        spacing: exportMode ? [0, 50, 0, 50] : [24, 16, 0, 30],
        backgroundColor: "transparent",
        animation: !exportMode,
        marginTop: exportMode ? 0 : 64,
      },

      title: {
        text: exportMode ? null : title,
        align: "left",
        x: 0,
        y: -3,
        margin: 12,
        style: { fontSize: "14px", fontWeight: "800", color: "#614a9e" },
      },

      exporting: { enabled: false },

      xAxis: {
        categories,
        lineWidth: 0,
        tickLength: 0,
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "12px",
            fontWeight: "800",
            color: "var(--text-color)",
          },
          formatter: function () {
            return exportMode ? truncate(this.value, 22) : truncate(this.value, 15);
          },
        },
      },

      yAxis: {
        title: { text: null },
        gridLineDashStyle: "ShortDot",
        gridLineColor: "rgba(17, 24, 39, 0.10)",
        labels: {
          style: {
            fontSize: exportMode ? "13px" : "12px",
            fontWeight: "800",
            color: "var(--text-color)",
          },
        },
      },

      legend: { enabled: false },

      tooltip: {
        useHTML: true,
        formatter: function () {
          const idx = this.point?.index ?? 0;
          const total = toNum(this.y);
          const cnt = toNum(counts[idx]);
          return `
            <div style="font-weight:800;margin-bottom:6px">${this.key}</div>
            <div>Valoração: <b>${total.toLocaleString("pt-BR")}</b></div>
            <div>Notícias: <b>${cnt.toLocaleString("pt-BR")}</b></div>
          `;
        },
      },

      plotOptions: {
        series: {
          animation: !exportMode,
          borderRadius: 6,
          pointPadding: exportMode ? 0.08 : 0.12,
          groupPadding: exportMode ? 0.04 : 0.08,
          dataLabels: {
            enabled: true,
            allowOverlap: true,
            crop: false,
            overflow: "allow",
            style: {
              fontWeight: "900",
              fontSize: exportMode ? "12px" : "11px",
              color: "var(--text-color)",
              textOutline: "none",
            },
            formatter: function () {
              const v = toNum(this.y);
              if (!v) return null;
              return v.toLocaleString("pt-BR");
            },
          },
        },
      },

      series: [
        {
          name: "Valoração",
          data: values,
          color: "#6D28D9",
        },
      ],

      credits: { enabled: false },
    };
  }, [categories, values, counts, title, filename, exportMode]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const t = setTimeout(() => {
      try {
        chart.reflow();

        if (exportMode) {
          const w = chart.renderTo?.clientWidth || null;
          chart.setSize(w, 560, false);
          chart.reflow();
        }
      } catch {}
    }, exportMode ? 60 : 0);

    return () => clearTimeout(t);
  }, [exportMode, categories, values]);

  const handleExportPNG = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.exportChart(
      { type: "image/png", filename },
      { chart: { backgroundColor: "var(--background-graphic)" } }
    );
  };

  const exportBtnStyle = {
    position: "absolute",
    top: 10,
    right: 10,
    zIndex: 5,

    height: 34,
    padding: "0 12px",
    borderRadius: 12,
    border: "none",
    background: "var(--bottom-dropdown-color)",
    color: "var(--text-color)",
    fontWeight: 900,
    fontSize: 12,

    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    transition: "transform .08s ease",
  };

  const exportBtnHoverStyle = {
    boxShadow: "0 6px 14px rgba(97, 74, 158, 0.15)",
    backgroundColor: "#614a9e",
    color: "white",
  };

  const iconStyle = {
    width: 18,
    height: 18,
    display: "grid",
    placeItems: "center",
    borderRadius: 8,
    background:
      "linear-gradient(135deg, var(--icon-gradient-start), var(--icon-gradient-end))",
    color: "var(--color-text-button-action)",
    fontSize: 12,
    lineHeight: 1,
    flex: "0 0 auto",
  };

  return (
    <LoadingBlock loading={loading} label="Carregando valoração por veículo...">
      <div style={wrapStyle}>
        {!exportMode ? (
          <ExportButton
            onClick={handleExportPNG}
            style={exportBtnStyle}
            hoverStyle={exportBtnHoverStyle}
            iconStyle={iconStyle}
          />
        ) : null}

        <HighchartsReact
          key={chartKey}
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: exportMode ? 560 : CHART_H } }}
        />
      </div>
    </LoadingBlock>
  );
}

function ExportButton({ onClick, style, hoverStyle, iconStyle }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, ...(hover ? hoverStyle : {}) }}
      title="Exportar PNG"
    >
      <span style={iconStyle}>⭳</span>
      Exportar PNG
    </button>
  );
}
